import React, { useState, useEffect } from "react";
import "./NewPatientRegistration.css";

const theme = {
  primaryDark: "#1b325f", primaryBlue: "#282f81", accentCyan: "#11abea",
  bgLight: "#f8fafc", cardBg: "#ffffff", textMain: "#334155",
  textMuted: "#64748b", borderLight: "#e2e8f0"
};

const Locations = ({ screenAction, triggerSave }) => {
  const initialFormState = {
    location_id: "",
    location_name: "",
    status: "Active"
  };

  const [formData, setFormData] = useState(initialFormState);
  const [isDisabled, setIsDisabled] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [locations, setLocations] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);

  // --- ERP COMPACT STYLES ---
  const compactRowStyle = { display: 'flex', alignItems: 'center', marginBottom: '6px' };
  const compactLabelStyle = { width: '110px', fontSize: '12px', color: theme.textMain, fontWeight: '600' };
  const compactInputStyle = { 
    flex: 1, padding: '4px 6px', fontSize: '12px', border: 'none', 
    borderBottom: `1px solid ${theme.borderLight}`, outline: 'none', 
    backgroundColor: isDisabled ? 'transparent' : '#fcfcfc' 
  };

  // 🔥 Fetch Next ID
  const fetchNextId = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    try {
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/location/id?employee_id=${user.id}`);
      const data = await res.json();
      setFormData(prev => ({ ...prev, location_id: data.location_id }));
    } catch (err) {
      console.error("Failed to fetch next ID", err);
    }
  };

  useEffect(() => {
    fetchNextId();
  }, []);

  const clearForm = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/location/id?employee_id=${user.id}`);
    const data = await res.json();

    setFormData({
      ...initialFormState,
      location_id: data.location_id
    });
    setSearchText("");
    setLocations([]);
  };

  // 🔍 Search
  const searchLocations = async (text) => {
    setSearchText(text);
    if (text.length < 2) {
      setLocations([]);
      return;
    }
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      const res = await fetch(`https://hmsapi.emedhatechnologies.com/master/api/api/locations/search?q=${text}&employee_id=${user.id}`);
      const data = await res.json();
      setLocations(data);
      setShowDropdown(true);
    } catch (err) {
      console.error(err);
    }
  };

  const selectLocation = (item) => {
    setFormData({
      location_id: item.location_id,
      location_name: item.location_name,
      status: item.status
    });
    setSearchText(`${item.location_id} | ${item.location_name}`);
    setShowDropdown(false);

    if (screenAction === "edit") setIsDisabled(false);
    if (screenAction === "view") setIsDisabled(true);
  };

  // 🔄 Mode control
  useEffect(() => {
    const init = async () => {
      if (screenAction === "add" || screenAction === "refresh") {
        clearForm();
        await fetchNextId();
        setIsDisabled(false);
      }
      if (screenAction === "edit") {
        clearForm();
        await fetchNextId();
        setIsDisabled(false);
      }
      if (screenAction === "view") {
        clearForm();
        setIsDisabled(true);
      }
      if (screenAction === "print") {
        printForm();
      }
    };
    init();
  }, [screenAction]);

  // 💾 Save
  useEffect(() => {
    if (triggerSave > 0 && screenAction === "save") {
      handleSubmit();
    }
  }, [triggerSave]);

  const handleSubmit = async () => {
    const user = JSON.parse(localStorage.getItem("user"));
    
    if(!formData.location_name) return alert("Location name is required.");

    const payload = { ...formData, created_by: user.id };
    delete payload.created_at;
    delete payload.updated_at;

    const method = formData.created_at || formData.hospital_id ? "PUT" : "POST";
    const url = method === "POST"
        ? "https://hmsapi.emedhatechnologies.com/master/api/api/location"
        : `https://hmsapi.emedhatechnologies.com/master/api/api/location/${formData.location_id}`;

    try {
      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        alert(`Location ${method === "POST" ? "Added" : "Updated"} Successfully`);
        if (method === "POST") clearForm();
      } else {
        alert("Error saving location");
      }
    } catch (err) {
      alert("Network error");
    }
  };

  // 🖨️ Print Form
  const printForm = () => {
    if (!formData.location_name) return alert("Select or create a location to print.");

    const newWindow = window.open("", "", "width=900,height=700");
    const today = new Date().toLocaleDateString();

    const htmlContent = `
      <html>
        <head>
          <title>Location Details - ${formData.location_id}</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; color: #333; margin: 0; padding: 40px; }
            .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #282f81; padding-bottom: 10px; margin-bottom: 20px; }
            .hospital-info h1 { margin: 0; color: #282f81; font-size: 24px; text-transform: uppercase; }
            .hospital-info p { margin: 2px 0; font-size: 12px; color: #7f8c8d; }
            .report-title { text-align: right; }
            .report-title h2 { margin: 0; color: #11abea; font-size: 18px; }
            .section-header { background: #f8fafc; padding: 8px 12px; font-weight: bold; font-size: 14px; border-left: 4px solid #282f81; margin-top: 20px; margin-bottom: 10px; text-transform: uppercase; }
            .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 10px; margin-bottom: 20px; }
            .info-item { font-size: 13px; display: flex; border-bottom: 1px dotted #ccc; padding: 6px 0; }
            .label { font-weight: 600; width: 160px; color: #555; }
            .value { flex: 1; font-weight: 500; color: #000; }
            .footer { margin-top: 50px; font-size: 10px; text-align: center; color: #95a5a6; border-top: 1px solid #eee; padding-top: 10px; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="hospital-info">
              <h1>EMEDHA CARE HOSPITAL</h1>
              <p>123 Medical Park, Health City - 560001</p>
            </div>
            <div class="report-title">
              <h2>LOCATION MASTER RECORD</h2>
              <p>Print Date: ${today}</p>
            </div>
          </div>

          <div class="section-header">Basic Information</div>
          <div class="info-grid">
            <div class="info-item"><span class="label">Location ID:</span> <span class="value">${formData.location_id}</span></div>
            <div class="info-item"><span class="label">Location Name:</span> <span class="value">${formData.location_name}</span></div>
            <div class="info-item"><span class="label">Status:</span> <span class="value">${formData.status}</span></div>
          </div>

          <div class="footer">
            This is a system-generated record from the Hospital Administration Module.
          </div>
        </body>
      </html>
    `;

    newWindow.document.write(htmlContent);
    newWindow.document.close();

    setTimeout(() => {
      newWindow.print();
      newWindow.close();
    }, 500);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="registration-container fade-in">

      {/* TOP ACTION BAR / SEARCH */}
      {(screenAction === "edit" || screenAction === "view") && (
        <div style={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-end', marginBottom: '20px', gap: '15px' }}>
          
          <div className="patient-search" style={{ position: 'relative', width: '350px', margin: 0 }}>
            <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 'bold', color: theme.primaryDark }}>Search Location</label>
            <input
              type="text"
              value={searchText}
              placeholder="Search by ID or Name"
              onChange={(e) => searchLocations(e.target.value)}
              style={{ width: '100%', padding: '8px 16px', borderRadius: '4px', border: `1px solid ${theme.borderLight}`, outline: 'none' }}
            />
            {showDropdown && locations.length > 0 && (
              <div className="patient-dropdown" style={{ position: 'absolute', top: '105%', left: 0, width: '100%', background: theme.cardBg, border: `1px solid ${theme.borderLight}`, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 100, maxHeight: '200px', overflowY: 'auto' }}>
                {locations.map((loc) => (
                  <div
                    key={loc.location_id}
                    className="patient-item"
                    onClick={() => selectLocation(loc)}
                    style={{ padding: '8px 12px', cursor: 'pointer', borderBottom: `1px solid ${theme.borderLight}`, fontSize: '12px' }}
                  >
                    {loc.location_id} | {loc.location_name}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* WhatsApp Icon - Only visible in VIEW mode when a location is loaded */}
  
        </div>
      )}

      <form className="luxury-form">
        
        <div className="form-section-header">
          <h3>Location Details</h3>
        </div>
        
        <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
          
          <div className="input-box read-only">
            <label>Location ID</label>
            <input type="text" name="location_id" value={formData.location_id || ""} disabled style={{ fontWeight: 'bold', color: theme.primaryBlue }} />
          </div>

          <div className="input-box">
            <label>Location Name <span style={{color:'red'}}>*</span></label>
            <input type="text" name="location_name" value={formData.location_name || ""} disabled={isDisabled} onChange={handleChange} placeholder="e.g. OT 1, ICU 1, Ward A" />
          </div>

          <div className="input-box">
            <label>Status</label>
            <select name="status" value={formData.status || "Active"} disabled={isDisabled} onChange={handleChange}>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
          
        </div>

      </form>
    </div>
  );
};

export default Locations;